/**
 * The screen's side of the tournament: holds the state, applies the server's events with the
 * same reducer the server uses, keeps a clock in step with the server, and queues anything the
 * screen wants to append until the server acknowledges it. A queued event survives a reload
 * (localStorage), so an entered score is never lost.
 */

import { apply } from "../../../engine/state";
import type { LoggedEvent, TournamentEvent, TournamentState } from "../../../engine/types";
import type { PinBook, PinKind, PinMode } from "../../../shared/pins";
import type { AdminName, Answer, ClientMessage, Identity, Links, Role, Screens, ServerMessage } from "../../../shared/protocol";
import type { Keeper } from "./session";
import type { Status, Transport } from "./transport";

export interface Queued {
  id: string;
  event: TournamentEvent;
  by: string;
  at: number;
}

export interface Snapshot {
  state: TournamentState | null;
  status: Status;
  identity: Identity | null;
  links: Links | null;
  /** Admin and watcher: which screens are connected, as the server last said. */
  screens: Screens | null;
  /** Admin and watcher: the last events of the log, as last asked for with askLog. */
  log: LoggedEvent[] | null;
  /** Events waiting for the server, oldest first. */
  queue: Queued[];
  /** The last refusal, for the screen to show, and the id it was for. */
  refusal: { id: string; reason: string; at: number } | null;
  /** Latest ack, so a screen can react to its own entry landing. */
  lastAck: { id: string; seq: number } | null;
  serverOffset: number;
  /** Admins: every name's PIN, as the server last said (decisions 39 and 40). */
  pins: PinBook | null;
  /** Admins: the last thing the server said about PINs (a name locked), to show once. */
  pinNews: { text: string; at: number } | null;
  /** This phone's session has stopped (its PIN was reset, the day started again): why. */
  signedOut: { as: PinKind; reason: string; at: number } | null;
  /** The projector: the owner's Note key asked for a picture of the wall, for the note `rid` (v26.10.027, temporary). */
  noteAsk: { rid: string; at: number } | null;
  /** The owner's admin screen: what the wall said it was showing, for the note `rid`. */
  noteWall: { rid: string; text: string } | null;
}

export interface Who {
  role: Role;
  court?: number;
  tournament: string;
  /** The link's token, so an admin's kept session is kept with the link it was for. */
  token?: string;
}

/**
 * What a phone is told when it comes back with a session that has stopped. It could be a reset PIN,
 * the day started again or the name taken off the list: the server no longer knows which, so the
 * words do not say.
 */
export const SESSION_STOPPED = "This phone was signed out. Pick your name again.";

/** How long a PIN request waits for its answer. */
const ASK_MS = 10_000;

/** Every screen pings this often; the server's pong, or anything else it says, shows the line is alive. */
export const PING_MS = 15_000;

/**
 * The watchdog: a ping this old with nothing at all heard since means the socket only says it is
 * open (a hall Wi-Fi that stays up and passes nothing), so it is dropped and opened again. Checked
 * at the next ping, so a dead line shows Offline within two pings.
 */
const SILENT_MS = 10_000;

type Listener = () => void;

/** The server's words when a screen's link has been replaced or taken away (and transport.ts's, after its check). */
export const LINK_STOPPED = /has been replaced|was taken away/;

/**
 * This page's link has stopped: the words it was told, or null while it works. One page is one
 * screen, so it is kept here, for the cover every screen but the admin's shows (LinkStopped.tsx).
 */
let stoppedReason: string | null = null;
const stoppedListeners = new Set<Listener>();

export function linkStopped(): string | null {
  return stoppedReason;
}

export function subscribeLinkStopped(fn: Listener): () => void {
  stoppedListeners.add(fn);
  return () => stoppedListeners.delete(fn);
}

function markStopped(reason: string): void {
  // the first words win: the server's, said as it happened, over the ones a later check works out
  if (stoppedReason !== null) return;
  stoppedReason = reason;
  for (const fn of stoppedListeners) fn();
}

function newId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export class TournamentClient {
  private snap: Snapshot = {
    state: null,
    status: "connecting",
    identity: null,
    links: null,
    screens: null,
    log: null,
    queue: [],
    refusal: null,
    lastAck: null,
    serverOffset: 0,
    pins: null,
    pinNews: null,
    signedOut: null,
    noteAsk: null,
    noteWall: null,
  };
  /** PIN requests waiting for their answer, by rid. */
  private asking = new Map<string, (a: Answer) => void>();
  private listeners = new Set<Listener>();
  private readonly storageKey: string;
  private flushing = false;
  /** The last server clock reading and when it arrived, so now() can run on from it. */
  private anchor: { server: number; local: number } | null = null;
  /** The watchdog: when the server was last heard from, and when the last ping went out on this socket. */
  private heardAt = 0;
  private pingedAt: number | null = null;

  constructor(
    private readonly transport: Transport,
    readonly who: Who,
    /** Who the screen says is acting; a player phone changes it when a different name is picked. */
    public by: string,
    /** How fast the server's clock runs against ours: 1 for the real server, the mock's speed otherwise. */
    private readonly clockRate = 1,
    /** Where this phone keeps its sessions for this tournament; none for screens that have no PIN. */
    readonly keeper: Keeper | null = null,
  ) {
    this.storageKey = `bd.queue.${who.tournament}.${who.role}.${who.court ?? 0}`;
    this.snap.queue = this.loadQueue();
  }

  /** The current snapshot. The same object until something changes (for useSyncExternalStore). */
  get(): Snapshot {
    return this.snap;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private set(patch: Partial<Snapshot>): void {
    this.snap = { ...this.snap, ...patch };
    for (const fn of this.listeners) fn();
  }

  /** Server time now: the last clock reading, run on at the server's rate. */
  now(): number {
    if (!this.anchor) return Date.now();
    return this.anchor.server + (Date.now() - this.anchor.local) * this.clockRate;
  }

  start(): void {
    this.transport.connect(
      (m) => this.onMessage(m),
      (status) => {
        // a ping belongs to the socket it went out on
        this.pingedAt = null;
        this.set({ status });
        if (status === "open") this.hello(this.snap.state?.seq ?? 0);
      },
    );
  }

  stop(): void {
    this.transport.close();
  }

  /** Hello, with this phone's session for the tournament when it has one. */
  private hello(since: number): void {
    const session = this.keeper?.sessionFor(this.who.role, this.who.token ?? "");
    this.transport.send({ kind: "hello", since, ...(session ? { session } : {}) });
  }

  /** The player's session this phone keeps, for the picture upload. */
  private playerSession(): string | null {
    return this.keeper?.player()?.session ?? null;
  }

  /**
   * One PIN request and its answer. A request that finds the socket closed, or gets no answer in
   * ten seconds, is answered here in words.
   */
  ask(m: DistributiveOmit<Extract<ClientMessage, { rid: string }>, "rid">): Promise<Answer> {
    const rid = newId();
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.asking.delete(rid);
        resolve({ kind: "answer", rid, ok: false, reason: "No answer. Check the connection and try again." });
      }, ASK_MS);
      this.asking.set(rid, (a) => {
        clearTimeout(timer);
        resolve(a);
      });
      if (!this.transport.send({ ...m, rid } as ClientMessage)) {
        clearTimeout(timer);
        this.asking.delete(rid);
        resolve({ kind: "answer", rid, ok: false, reason: "Not connected. Try again in a moment." });
      }
    });
  }

  /** Does this player have a PIN yet? */
  pinCheck(player: string): Promise<Answer> {
    return this.ask({ kind: "pin.check", player });
  }

  /** Set or type a player's PIN; the right one makes this phone that player. */
  claim(player: string, pin: string, mode: PinMode): Promise<Answer> {
    return this.ask({ kind: "pin.claim", player, pin, mode });
  }

  /** Set or type this admin screen's PIN; the right one unlocks it. */
  adminPin(pin: string, mode: PinMode): Promise<Answer> {
    return this.ask({ kind: "admin.pin", pin, mode });
  }

  /** The hidden way in: the named admins who have set a PIN. */
  adminNames(): Promise<Answer & { admins?: AdminName[] }> {
    return this.ask({ kind: "admin.names" });
  }

  /** The hidden way in: an admin PIN for a picked admin, or for the admin link this phone opened before. */
  adminOpen(pin: string, to: { admin: string } | { link: string }): Promise<Answer> {
    return this.ask({ kind: "admin.open", pin, ...to });
  }

  /** Admin only: reset a player's or an admin's PIN. */
  resetPin(as: PinKind, id: string): Promise<Answer> {
    return this.ask({ kind: "pin.reset", as, id });
  }

  /** "Not you?": this phone forgets who it is, here and on the server. */
  forget(): void {
    this.transport.send({ kind: "session.end" });
    this.keeper?.savePlayer(null);
  }

  /** The signed-out screen has been read. */
  clearSignedOut(): void {
    this.set({ signedOut: null });
  }

  /**
   * Ask the server to append an event. Queued events are kept until acked or refused and resent
   * after every reconnect; a plain send that finds the socket closed is refused at once.
   */
  send(event: TournamentEvent, opts: { queue?: boolean; id?: string } = {}): string {
    const id = opts.id ?? newId();
    const item: Queued = { id, event, by: this.by, at: Date.now() };
    if (opts.queue) {
      this.set({ queue: [...this.snap.queue, item] });
      this.saveQueue();
      this.flush();
      return id;
    }
    const sent = this.transport.send({ kind: "append", id, event, by: this.by });
    if (!sent) this.set({ refusal: { id, reason: "Not connected. Try again in a moment.", at: Date.now() } });
    return id;
  }

  regenerate(role: Role): void {
    this.transport.send({ kind: "regenerate", role });
  }

  askLinks(): void {
    this.transport.send({ kind: "links" });
  }

  /** Admin only: give someone their own admin link. Every admin screen is sent the new list. */
  addAdmin(name: string): void {
    this.transport.send({ kind: "admins.add", name });
  }

  /** Admin only: take a named admin's link away. */
  removeAdmin(id: string): void {
    this.transport.send({ kind: "admins.remove", id });
  }

  /** Admin only: a new link for one named admin. */
  renewAdmin(id: string): void {
    this.transport.send({ kind: "admins.renew", id });
  }

  /** Admin and watcher: ask for the last events of the log; the answer lands in the snapshot's `log`. */
  askLog(last: number): void {
    this.transport.send({ kind: "log", last });
  }

  uploadPhoto(playerId: string, jpeg: Blob): Promise<string | null> {
    return this.transport.uploadPhoto(playerId, jpeg, this.playerSession());
  }

  photoUrl(playerId: string, version: number): string {
    return this.transport.photoUrl(playerId, version);
  }

  /** Admin only: wipe the day and start again. The screen must have asked for the word RESET. */
  reset(): void {
    this.transport.send({ kind: "reset", confirm: "RESET" });
  }

  /** The owner's Note key: ask the wall for its picture for the note `rid`. False when not connected. */
  noteAskWall(rid: string): boolean {
    return this.transport.send({ kind: "feedback.ask", rid });
  }

  /** Every PING_MS: a ping, unless the last one has gone unanswered, when the socket is started again. */
  ping(): void {
    const now = Date.now();
    if (this.snap.status === "open" && this.pingedAt !== null && now - this.pingedAt >= SILENT_MS && this.heardAt < this.pingedAt) {
      this.pingedAt = null;
      this.transport.restart();
      return;
    }
    this.pingedAt = this.transport.send({ kind: "ping", t: now }) ? now : null;
  }

  private flush(): void {
    if (this.flushing || this.snap.status !== "open") return;
    this.flushing = true;
    try {
      for (const item of this.snap.queue) {
        if (!this.transport.send({ kind: "append", id: item.id, event: item.event, by: item.by })) break;
      }
    } finally {
      this.flushing = false;
    }
  }

  private onMessage(m: ServerMessage): void {
    this.heardAt = Date.now();
    if ("serverNow" in m) {
      this.anchor = { server: m.serverNow, local: Date.now() };
      this.set({ serverOffset: m.serverNow - Date.now() });
    }
    switch (m.kind) {
      case "welcome": {
        this.set({ state: m.state, identity: m.identity });
        if (m.session === "gone") this.sessionGone(m.identity);
        if (m.identity.role === "admin") this.keepAdminLink(m.identity);
        this.flush();
        break;
      }
      case "identity": {
        this.set({ identity: m.identity });
        if (m.identity.role === "admin") this.keepAdminLink(m.identity);
        break;
      }
      case "session": {
        if (m.as === "player" && m.player) {
          this.keeper?.savePlayer({ session: m.session, player: m.player });
        } else if (m.as === "admin" && m.token) {
          this.keeper?.saveAdmin({ token: m.token, session: m.session, name: m.name ?? null, owner: m.owner === true });
        }
        if (this.snap.signedOut?.as === m.as) this.set({ signedOut: null });
        break;
      }
      case "signed.out": {
        if (m.as === "player") this.keeper?.savePlayer(null);
        else {
          const kept = this.keeper?.admin();
          if (kept) this.keeper?.saveAdmin({ ...kept, session: null });
        }
        this.set({ signedOut: { as: m.as, reason: m.reason, at: Date.now() } });
        break;
      }
      case "pins": {
        this.set({ pins: m.book, ...(m.news ? { pinNews: { text: m.news, at: Date.now() } } : {}) });
        break;
      }
      case "answer": {
        const done = this.asking.get(m.rid);
        if (done) {
          this.asking.delete(m.rid);
          done(m);
        }
        break;
      }
      case "events": {
        let state = this.snap.state;
        if (!state) {
          this.hello(0);
          break;
        }
        for (const e of m.events as LoggedEvent[]) {
          if (e.seq <= state.seq) continue;
          if (e.seq !== state.seq + 1) {
            // a gap: ask for everything since what we hold
            this.hello(state.seq);
            return;
          }
          try {
            state = apply(state, e);
          } catch {
            this.hello(0);
            return;
          }
        }
        this.set({ state });
        break;
      }
      case "ack": {
        const queue = this.snap.queue.filter((q) => q.id !== m.id);
        this.set({ queue, lastAck: { id: m.id, seq: m.seq } });
        this.saveQueue();
        break;
      }
      case "refused": {
        const queue = this.snap.queue.filter((q) => q.id !== m.id);
        this.set({ queue, refusal: { id: m.id, reason: m.reason, at: Date.now() } });
        this.saveQueue();
        break;
      }
      case "links": {
        this.set({ links: m.links });
        break;
      }
      case "screens": {
        this.set({ screens: m.screens });
        break;
      }
      case "log": {
        this.set({ log: m.events });
        break;
      }
      case "error": {
        this.set({ refusal: { id: "", reason: m.reason, at: Date.now() } });
        // the link has stopped: no more tries on it (anything queued stays here unsent, which is right)
        if (LINK_STOPPED.test(m.reason)) {
          markStopped(m.reason);
          this.transport.close();
        }
        break;
      }
      case "feedback.snap": {
        this.set({ noteAsk: { rid: m.rid, at: Date.now() } });
        break;
      }
      case "feedback.wall": {
        this.set({ noteWall: { rid: m.rid, text: m.text } });
        break;
      }
      case "pong":
        break;
      default:
        break;
    }
  }

  /** The session this phone came back with has stopped: forget it, and a player's phone says why. */
  private sessionGone(identity: Identity): void {
    if (identity.role === "player") {
      this.keeper?.savePlayer(null);
      this.set({ signedOut: { as: "player", reason: SESSION_STOPPED, at: Date.now() } });
    } else if (identity.role === "admin") {
      const kept = this.keeper?.admin();
      if (kept) this.keeper?.saveAdmin({ ...kept, session: null });
    }
  }

  /**
   * An admin link has opened on this phone: keep its token (and who it is), with or without a
   * session, so the hidden way in from the players' side asks for this admin's PIN.
   */
  private keepAdminLink(identity: Identity): void {
    const token = this.who.token;
    if (!this.keeper || !token) return;
    const kept = this.keeper.admin();
    const name = identity.owner === false ? identity.name ?? null : null;
    const owner = identity.owner !== false;
    if (kept && kept.token === token && kept.name === name && kept.owner === owner) return;
    this.keeper.saveAdmin({ token, session: kept && kept.token === token ? kept.session : null, name, owner });
  }

  /** The owner's link was replaced from this phone: its session moves to the new link with it. */
  moveAdminLink(token: string): void {
    const kept = this.keeper?.admin();
    if (kept) this.keeper?.saveAdmin({ ...kept, token });
  }

  private loadQueue(): Queued[] {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Queued[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private saveQueue(): void {
    try {
      if (this.snap.queue.length) localStorage.setItem(this.storageKey, JSON.stringify(this.snap.queue));
      else localStorage.removeItem(this.storageKey);
    } catch {
      // storage can be unavailable (private mode); the queue still lives in memory
    }
  }
}

export type { ClientMessage, ServerMessage };

/** Omit that keeps a union a union. */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
